import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Field,
  Option,
} from '@fluentui/react-components';
import { type UseMutationResult } from '@tanstack/react-query';
import { type TaxReturn } from '../../../types';

export interface EditSignatureStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSignatureStatus: string;
  updateMutation: UseMutationResult<unknown, Error, Partial<TaxReturn>>;
}

export function EditSignatureStatusDialog({
  open,
  onOpenChange,
  initialSignatureStatus,
  updateMutation,
}: EditSignatureStatusDialogProps) {
  const [selectedSignatureStatus, setSelectedSignatureStatus] = useState(initialSignatureStatus);

  useEffect(() => {
    if (open) {
      setSelectedSignatureStatus(initialSignatureStatus);
    }
  }, [open, initialSignatureStatus]);

  const handleSave = () => {
    const updates: Partial<TaxReturn> = {
      signatureStatus: selectedSignatureStatus as 'pending' | 'sent' | 'signed',
    };

    if (selectedSignatureStatus === 'sent') {
      updates.signatureSentDate = new Date().toISOString();
    } else if (selectedSignatureStatus === 'signed') {
      updates.signatureCompletedDate = new Date().toISOString();
    }

    updateMutation.mutate(updates, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Update Signature Status</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Field label="Signature Status">
                <Dropdown
                  value={selectedSignatureStatus === 'signed' ? 'Signed' :
                         selectedSignatureStatus === 'sent' ? 'Sent' : 'Pending'}
                  selectedOptions={selectedSignatureStatus ? [selectedSignatureStatus] : []}
                  onOptionSelect={(_, data) => setSelectedSignatureStatus(data.optionValue as string)}
                  style={{ width: '100%' }}
                >
                  <Option value="pending">Pending</Option>
                  <Option value="sent">Sent</Option>
                  <Option value="signed">Signed</Option>
                </Dropdown>
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!selectedSignatureStatus || updateMutation.isPending}
              onClick={handleSave}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
